import { Popper } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { measureActions, selectIsMenuOpen } from "features/measure";
import { Picker } from "features/render";
import { sectionPlaneActions, selectIsSectionMenuOpen } from "features/sectionPlane";
import CoordinatesIcon from "media/icons/coordinates.svg?react";
import LaserIcon from "media/icons/laser.svg?react";
import PointToPointIcon from "media/icons/point-to-point.svg?react";
import RulerIcon from "media/icons/ruler.svg?react";
import SectionIcon from "media/icons/section.svg?react";
import SectionPlaneIcon from "media/icons/section-plane.svg?react";

import { QUICK_ACCESS_TOOLBAR } from "../constants";
import { Toolbar } from "../Toolbar";
import { MeasureFlyoutToolbar } from "./MeasureFlyoutToolbar";
import { SectionFlyoutToolbar } from "./SectionFlyoutToolbar";

interface ToolsMenuToolbarProps {
    picker: Picker;
    id?: string;
}

const measurementPickers = [Picker.OutlineLaser, Picker.Coordinates, Picker.PointLine, Picker.Dimension];

/**
 * Combined Measure and Section toolbar,
 * each with their own flyout menu.
 */
export function ToolsMenuToolbar({ picker, id = "tools-menu-toolbar" }: ToolsMenuToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();
    const isMeasureMenuOpen = useAppSelector(selectIsMenuOpen);
    const isSectionMenuOpen = useAppSelector(selectIsSectionMenuOpen);

    const measureBtnRef = useRef<HTMLButtonElement>(null);
    const sectionBtnRef = useRef<HTMLButtonElement>(null);
    const prevPickerRef = useRef<Picker>(picker);

    const primaryToolbarId = `${id}-primary`;
    const measureFlyoutId = `${id}-measure-flyout`;
    const sectionFlyoutId = `${id}-section-flyout`;

    const isMeasureActive = Boolean(isMeasureMenuOpen || measurementPickers.includes(picker));
    const isSectionActive = Boolean(isSectionMenuOpen || picker === Picker.SectionPlane);

    const allToolsPickers = useMemo(() => [...measurementPickers, Picker.SectionPlane], []);

    useEffect(() => {
        const prevPicker = prevPickerRef.current;
        prevPickerRef.current = picker;

        if (allToolsPickers.includes(prevPicker) && !allToolsPickers.includes(picker)) {
            dispatch(measureActions.setMenuOpen(false));
            dispatch(sectionPlaneActions.setMenuOpen(false));
        }
    }, [picker, dispatch, allToolsPickers]);

    const measureIcon = useMemo(() => {
        switch (picker) {
            case Picker.OutlineLaser:
                return <LaserIcon />;
            case Picker.Coordinates:
                return <CoordinatesIcon />;
            case Picker.PointLine:
                return <PointToPointIcon />;
            case Picker.Dimension:
                return <RulerIcon />;
            default:
                return <RulerIcon />;
        }
    }, [picker]);

    const sectionIcon = useMemo(
        () => (picker === Picker.SectionPlane ? <SectionPlaneIcon /> : <SectionIcon />),
        [picker],
    );

    const buttons: ToolbarButtonEntry[] = useMemo(() => {
        return [
            {
                config: {
                    id: "measure-menu-toggle",
                    icon: measureIcon,
                    onClick: () => {
                        dispatch(measureActions.setMenuOpen(!isMeasureMenuOpen));
                        void track(Events.toolbar.measureTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Measure Tools",
                    ref: measureBtnRef,
                    hasExpander: true,
                    expanderPosition: "bottom-left",
                },
                state: {
                    active: isMeasureActive,
                    disabled: false,
                    visible: true,
                    expanderActive: isMeasureActive,
                },
            },
            {
                config: {
                    id: "section-menu-toggle",
                    icon: sectionIcon,
                    onClick: () => {
                        dispatch(sectionPlaneActions.setMenuOpen(!isSectionMenuOpen));
                        void track(Events.toolbar.sectionTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Section Tools",
                    ref: sectionBtnRef,
                    hasExpander: true,
                    expanderPosition: "bottom-left",
                },
                state: {
                    active: isSectionActive,
                    disabled: false,
                    visible: true,
                    expanderActive: isSectionActive,
                },
            },
        ];
    }, [isMeasureActive, isSectionActive, dispatch, isMeasureMenuOpen, isSectionMenuOpen, measureIcon, sectionIcon]);

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: primaryToolbarId,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(primaryToolbarId));
        };
    }, [toolbarDispatch, primaryToolbarId, buttons]);

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.bulkUpdateButtonStates(primaryToolbarId, {
                "measure-menu-toggle": {
                    active: isMeasureActive,
                    expanderActive: isMeasureActive,
                },
                "section-menu-toggle": {
                    active: isSectionActive,
                    expanderActive: isSectionActive,
                },
            }),
        );
    }, [toolbarDispatch, primaryToolbarId, isMeasureActive, isSectionActive]);

    const flyoutPopperSx = { zIndex: QUICK_ACCESS_TOOLBAR.FLYOUT_Z_INDEX };

    return (
        <>
            <Toolbar id={primaryToolbarId} />
            <Popper
                open={isMeasureMenuOpen}
                anchorEl={measureBtnRef.current}
                placement="left-start"
                sx={flyoutPopperSx}
            >
                <MeasureFlyoutToolbar
                    onToolSelect={() => dispatch(measureActions.setMenuOpen(false))}
                    id={measureFlyoutId}
                    primaryToolbarId={primaryToolbarId}
                />
            </Popper>
            <Popper
                open={isSectionMenuOpen}
                anchorEl={sectionBtnRef.current}
                placement="left-start"
                sx={flyoutPopperSx}
            >
                <SectionFlyoutToolbar
                    onToolSelect={() => dispatch(sectionPlaneActions.setMenuOpen(false))}
                    id={sectionFlyoutId}
                    primaryToolbarId={primaryToolbarId}
                />
            </Popper>
        </>
    );
}
